import type { CompetitionDetailDto, RoundDto } from '@napaicp/schemas'
import { hasPlatformPermission, PLATFORM_PERMISSIONS, type GlobalRole } from '@napaicp/shared'
import { useQueries } from '@tanstack/react-query'
import { roundsQueryOptions, useCompetitions } from '@/features/competitions/api'
import { competitionKeys } from '@/features/competitions/query-keys'
import {
  resolveScheduleWithRounds,
  type CompetitionScheduleDates,
} from '@/features/public/competition-lifecycle'
import { useSession } from '@/features/session/use-session'
import { apiRequest } from '@/lib/api-client'
import { hasAnyPlatformReadAccess } from '@/lib/platform-access'
import {
  deriveAdminPublishAction,
  deriveJudgeAction,
  deriveRecentCompetitionViewModel,
  deriveRegistrationUpdateAction,
  deriveStudentAction,
  derivePlatformGovernanceAction,
  deriveWorkspaces,
  sortActions,
  type HomeAction,
  type HomeWorkspace,
  type RecentCompetitionViewModel,
} from './derive-home'
import { incompleteRegistrationLabels } from '@/features/registration/completeness'
import { usePendingCompetitionRequestCount } from './api'
import {
  judgeAssignmentsQueryOptions,
  mySubmissionsQueryOptions,
  registrationAnswersQueryOptions,
  registrationFieldsQueryOptions,
  registrationFormsQueryOptions,
  teamStatusQueryOptions,
} from './query-options'

const ACTIVE_STATUSES = new Set(['registration_open', 'submission_open', 'judging'])
/** UX-045's pre-existing SPEC-GAP, unchanged by this task: nothing tracks last-accessed-at per user per competition, so "recently used" is server list order. */
const RECENT_COUNT = 3

export interface HomeOverview {
  isPending: boolean
  actions: HomeAction[]
  workspaces: HomeWorkspace[]
  recent: RecentCompetitionViewModel[]
}

function toSchedule(
  detail: CompetitionDetailDto,
  rounds: RoundDto[] | undefined,
): CompetitionScheduleDates {
  return resolveScheduleWithRounds(detail.status, detail, rounds)
}

/**
 * The one hook `signed-in-dashboard.tsx` and its sections read from — every
 * per-competition fetch this page needs is fanned out here via `useQueries`
 * (a competition's `useCompetition`/`useJudgeAssignments`/etc. cannot be
 * called as hooks in a `.map()` loop; `useQueries` is the array-of-queries
 * form of the same client). Bounded to `registration_open`/`submission_open`/
 * `judging` competitions where the caller actually holds a role that could
 * produce an action, plus the "recently used" slice for its milestone date —
 * a real multi-role account holds a handful of these, not hundreds.
 *
 * Round-level schedule overrides are applied (`resolveScheduleWithRounds`,
 * BR-118/PR-161): each fanned-out competition also fetches its rounds, and
 * the current round's dates are merged over the envelope before feeding
 * `getCompetitionNextMilestone` and the student-action derivation. While a
 * rounds query is still loading it degrades to the envelope.
 */
export function useHomeOverview(): HomeOverview {
  const session = useSession()
  const competitions = useCompetitions()
  const globalRole: GlobalRole =
    session.data?.authenticated === true ? (session.data.user?.globalRole ?? 'user') : 'user'
  const isPlatformAdmin = hasAnyPlatformReadAccess(globalRole)
  const canApproveCompetitions = hasPlatformPermission(
    { globalRole },
    PLATFORM_PERMISSIONS.COMPETITIONS_APPROVE,
  )

  const data = competitions.data ?? []
  const recent = data.slice(0, RECENT_COUNT)
  const studentActive = data.filter(
    (c) => c.roles.includes('student') && ACTIVE_STATUSES.has(c.status),
  )
  const judgeActive = data.filter((c) => c.roles.includes('judge') && ACTIVE_STATUSES.has(c.status))
  const draftAdmin = data.filter(
    (c) =>
      c.status === 'draft' && (c.roles.includes('competition_admin') || c.roles.includes('staff')),
  )

  const detailIds = Array.from(
    new Set([...recent.map((c) => c.id), ...studentActive.map((c) => c.id)]),
  )
  const detailQueries = useQueries({
    queries: detailIds.map((id) => ({
      queryKey: competitionKeys.detail(id),
      queryFn: () => apiRequest<CompetitionDetailDto>(`/api/competitions/${id}`),
    })),
  })
  const detailById = new Map(detailIds.map((id, index) => [id, detailQueries[index]?.data]))
  // BR-118/PR-161: the current round's dates, not the stale envelope, feed the schedule.
  const roundsQueries = useQueries({ queries: detailIds.map((id) => roundsQueryOptions(id)) })
  const roundsById = new Map(detailIds.map((id, index) => [id, roundsQueries[index]?.data]))

  const studentIds = studentActive.map((c) => c.id)
  const teamQueries = useQueries({ queries: studentIds.map((id) => teamStatusQueryOptions(id)) })
  const formsQueries = useQueries({
    queries: studentIds.map((id) => registrationFormsQueryOptions(id)),
  })
  const formIdByCompetition = new Map(
    studentIds.map((id, index) => {
      const form = formsQueries[index]?.data?.[0]
      return [id, form !== undefined && form.currentVersion !== null ? form.id : undefined] as const
    }),
  )
  const answersQueries = useQueries({
    queries: studentIds.map((id) =>
      registrationAnswersQueryOptions(id, formIdByCompetition.get(id)),
    ),
  })
  const submissionsQueries = useQueries({
    queries: studentIds.map((id) => mySubmissionsQueryOptions(id)),
  })
  // AC-373: the current form's fields, to tell whether a saved registration still satisfies it.
  const fieldsQueries = useQueries({
    queries: studentIds.map((id, index) =>
      registrationFieldsQueryOptions(
        id,
        formIdByCompetition.get(id),
        formsQueries[index]?.data?.[0]?.currentVersion?.id,
      ),
    ),
  })

  const judgeIds = judgeActive.map((c) => c.id)
  const judgeAssignmentQueries = useQueries({
    queries: judgeIds.map((id) => judgeAssignmentsQueryOptions(id)),
  })

  const platformPendingCount = usePendingCompetitionRequestCount(canApproveCompetitions)
  const platformGovernanceAction = derivePlatformGovernanceAction(
    canApproveCompetitions ? platformPendingCount : 0,
  )

  const actions: HomeAction[] = []
  if (platformGovernanceAction !== null) actions.push(platformGovernanceAction)

  for (const competition of draftAdmin) {
    const action = deriveAdminPublishAction(competition)
    if (action !== null) actions.push(action)
  }

  judgeActive.forEach((competition, index) => {
    const assignments = judgeAssignmentQueries[index]?.data ?? []
    const pendingCount = assignments.filter((assignment) => assignment.status === 'active').length
    const action = deriveJudgeAction(competition, pendingCount)
    if (action !== null) actions.push(action)
  })

  const now = new Date()
  studentActive.forEach((competition, index) => {
    const detail = detailById.get(competition.id)
    if (detail === undefined) return
    const team = teamQueries[index]?.data
    const formId = formIdByCompetition.get(competition.id)
    const answers = answersQueries[index]?.data
    const submissions = submissionsQueries[index]?.data ?? []
    const latestSubmission = submissions.at(-1)

    const action = deriveStudentAction(
      {
        competitionId: competition.id,
        competitionName: competition.name,
        teamRequired: detail.teamMaxMembers > 1,
        registrationHasForm: formId !== undefined,
        registrationCompleted: formId === undefined || answers != null,
        hasTeam: detail.teamMaxMembers > 1 ? team != null : null,
        submissionStatus: latestSubmission?.status ?? null,
        wasSubmittedBefore: latestSubmission?.submittedAt != null,
        isFinalRound: latestSubmission?.isFinalRound === true,
        schedule: toSchedule(detail, roundsById.get(competition.id)),
      },
      now,
    )
    if (action !== null) actions.push(action)

    // AC-373: an extra action when the saved registration no longer satisfies the form.
    const updateAction = deriveRegistrationUpdateAction(
      {
        competitionId: competition.id,
        competitionName: competition.name,
        incompleteLabels: incompleteRegistrationLabels(fieldsQueries[index]?.data, answers),
        schedule: toSchedule(detail, roundsById.get(competition.id)),
      },
      now,
    )
    if (updateAction !== null) actions.push(updateAction)
  })

  const workspaces = deriveWorkspaces(
    data,
    isPlatformAdmin,
    isPlatformAdmin ? platformPendingCount : 0,
  )

  const recentViewModels = recent.map((competition) => {
    const detail = detailById.get(competition.id)
    return deriveRecentCompetitionViewModel(
      competition,
      detail === undefined ? null : toSchedule(detail, roundsById.get(competition.id)),
      globalRole,
    )
  })

  return {
    isPending: competitions.isPending,
    actions: sortActions(actions),
    workspaces,
    recent: recentViewModels,
  }
}
